// @ts-nocheck
import * as Sentry from '@sentry/browser';
import HttpStatusCode from './HttpStatusCode';
import type { GraphQLRestDataSourceError } from './Types';
import {
  ApolloClient,
  from,
  InMemoryCache,
  type NormalizedCacheObject,
} from '@apollo/client';
import type {
  ApolloQueryResult,
  OperationVariables,
} from '@apollo/client/core/types';
import type {
  MutationOptions,
  QueryOptions,
} from '@apollo/client/core/watchQueryOptions';
import { setContext } from '@apollo/client/link/context';
import type { FetchResult } from '@apollo/client/link/core';
import { onError } from '@apollo/client/link/error';
import { getOperationName } from '@apollo/client/utilities';
import { createUploadLink } from 'apollo-upload-client';
import fetch from 'cross-fetch';
import type { DocumentNode, GraphQLError } from 'graphql';

import { DatadogTrack } from '@cobliteam/datadog-rum';
import { Environment } from '@cobliteam/react-environments';

import { getAuthHeaders } from '../account/session/AuthToken';
import { SessionManager } from '../account/session/SessionManager';
import { getOpenTelemetryBaggageHttpHeaders } from '../openTelemetryBaggage';

const env = Environment.getInstance().getEnvironment();

export class GraphQLRequester {
  private client: ApolloClient<NormalizedCacheObject>;

  private sessionManager: SessionManager;

  constructor(
    client: ApolloClient<NormalizedCacheObject>,
    sessionManager: SessionManager
  ) {
    this.client = client;
    this.sessionManager = sessionManager;
  }

  private logRequestToDatadog = (node: DocumentNode) => {
    const operationName = getOperationName(node);
    if (operationName) {
      DatadogTrack.action(`GraphQL request in operation ${operationName}`);
    }
  };

  public query = <T, TVariables = OperationVariables>(
    options: QueryOptions<TVariables>
  ): Promise<T> => {
    this.logRequestToDatadog(options.query);
    return this.sessionManager.maybeRenewAccessToken().then(() =>
      this.client
        .query({
          ...options,
          fetchPolicy: 'no-cache',
        })
        .then(this.parseGraphQLResponse)
        .catch((error) => {
          Sentry.captureException(error);
          throw error;
        })
    );
  };

  public mutate = <T, TVariables = OperationVariables>(
    options: MutationOptions<any, TVariables>
  ): Promise<T> => {
    this.logRequestToDatadog(options.mutation);
    return this.sessionManager.maybeRenewAccessToken().then(() =>
      this.client
        .mutate(options)
        .then(this.parseGraphQLResponse)
        .catch((error) => {
          Sentry.captureException(error);
          throw error;
        })
    );
  };

  private parseGraphQLResponse = <T>(
    response: FetchResult<T> | ApolloQueryResult<T>
  ): Promise<T> => {
    let errors;
    if (response.errors?.length) {
      errors = response.errors.map(this.parseGraphQLError);
    }
    if (!response.data) {
      if (errors) {
        return Promise.reject({ errors });
      }
      return Promise.reject(new Error('Query did not map any data'));
    }
    return Promise.resolve({ errors, ...response.data });
  };

  private parseGraphQLError = (
    error: GraphQLError
  ): GraphQLRestDataSourceError => {
    const httpErrorData = error.extensions?.response || {};
    const errorCode = error.extensions?.code;
    return {
      message: error.message,
      code: errorCode,
      ...httpErrorData,
    };
  };
}

const uploadLink = createUploadLink({
  uri: env.graphqlHost,
  fetch,
});

const authLink = setContext((_, { headers }) => {
  return {
    headers: {
      ...headers,
      ...getAuthHeaders(),
      ...getOpenTelemetryBaggageHttpHeaders(),
    },
  };
});

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  if (
    graphQLErrors?.some(
      (error) => error.extensions.response?.status === HttpStatusCode.NOT_FOUND
    )
  ) {
    return;
  }

  const { operationName } = operation;

  if (graphQLErrors && graphQLErrors.length > 0) {
    const ddError = new Error(
      `Request error in operation ${operationName}, message=<${graphQLErrors[0].message}>`
    );
    ddError.name = '[GraphQL error]';
    ddError.stack = `${ddError.stack}\n${graphQLErrors[0].stack}`;
    DatadogTrack.error(ddError);
  }

  if (networkError) {
    const ddError = new Error(
      `Network request error in operation ${operationName}, message=<${networkError.message}>`
    );
    ddError.name = '[GraphQL Network error]';
    ddError.stack = `${ddError.stack}\n${networkError.stack}`;
    DatadogTrack.error(ddError);
  }
});

const instance = new GraphQLRequester(
  new ApolloClient({
    cache: new InMemoryCache(),
    link: from([authLink, errorLink, uploadLink]),
    defaultOptions: {
      query: {
        errorPolicy: 'all',
      },
      mutate: {
        errorPolicy: 'all',
      },
    },
  }),
  new SessionManager({ host: env.herbieApiHost })
);

export default instance;
